import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * 流式文本打字机效果组件
 *
 * 逐字追加字符, 模拟 Agent "边思考边输出" 的体验
 *
 * 用法:
 *   <StreamOutput content="全量文本" speed={30} onComplete={fn} />
 *
 * @param {string}   content    — 要逐字显示的完整文本
 * @param {number}   speed      — 打字速度 (ms/字符), 默认 30
 * @param {boolean}  paused     — 暂停打字
 * @param {function} onComplete — 打字完毕后回调
 * @param {boolean}  blinkCursor — 是否闪烁光标, 默认 true
 * @param {string}   prefixIcon  — 开头图标 (emoji 或 ReactNode)
 */
export default function StreamOutput({
  content = '',
  speed = 30,
  paused = false,
  onComplete,
  blinkCursor = true,
  prefixIcon = null,
}) {
  const [displayed, setDisplayed] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const indexRef = useRef(0);
  const timerRef = useRef(null);
  const completedRef = useRef(false);
  const prevContentRef = useRef('');

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 核心: 逐字追加
  useEffect(() => {
    // 当 content 重置时 (新段落), 清空状态重新开始
    if (content !== prevContentRef.current && indexRef.current >= prevContentRef.current.length) {
      // 上一段已完成, 新的 content 开始了
      if (content.length < prevContentRef.current.length) {
        indexRef.current = 0;
        setDisplayed('');
        completedRef.current = false;
      }
    }
    prevContentRef.current = content;

    if (!content) {
      setDisplayed('');
      indexRef.current = 0;
      completedRef.current = false;
      return;
    }

    if (paused) return;

    // 内容比显示的多 → 开始打字
    if (content.length > displayed.length) {
      setIsTyping(true);
      clearTimer();

      timerRef.current = setInterval(() => {
        indexRef.current += 1;
        if (indexRef.current >= content.length) {
          setDisplayed(content);
          setIsTyping(false);
          clearTimer();
          if (!completedRef.current) {
            completedRef.current = true;
            onComplete?.();
          }
        } else {
          setDisplayed(content.slice(0, indexRef.current));
        }
      }, speed);
    }

    return clearTimer;
  }, [content, speed, paused, clearTimer, onComplete, displayed.length]);

  // 卸载清理
  useEffect(() => () => clearTimer(), [clearTimer]);

  return (
    <span style={styles.wrapper}>
      {prefixIcon && <span style={styles.prefix}>{prefixIcon}</span>}
      <Text style={styles.text}>
        {displayed}
      </Text>
      {/* 闪烁光标 */}
      {isTyping && blinkCursor && (
        <span style={styles.cursor} />
      )}
      {/* 等待图标 */}
      {!content && !isTyping && (
        <LoadingOutlined style={{ marginLeft: 6, fontSize: 12, color: 'var(--accent)' }} />
      )}
    </span>
  );
}

// ─── 样式 ──────────────────────────────────────────────────────────────────
const styles = {
  wrapper: {
    display: 'inline',
    position: 'relative',
  },
  prefix: {
    marginRight: 6,
    verticalAlign: 'middle',
  },
  text: {
    color: 'var(--text-primary, #e8e8e8)',
    fontSize: 13,
    lineHeight: 1.7,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  cursor: {
    display: 'inline-block',
    width: 2,
    height: 14,
    marginLeft: 1,
    verticalAlign: 'middle',
    background: 'var(--accent, #e8d28a)',
    animation: 'streamBlink 0.8s step-end infinite',
  },
};

// ─── 闪烁动画 (通过 style 标签注入) ─────────────────────────────────────────
// 使用者可将以下代码放入全局 CSS, 或在父组件中定义:
// @keyframes streamBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }

export { styles as STREAM_OUTPUT_STYLES };
